import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import QRCode from 'qrcode'
import { useRealtimeSubscription } from '@dockethub/realtime'
import { getOrder, payOrderDemo, type OrderDto } from '../lib/api.js'
import { useSessionStore } from '../stores/session.store.js'
import { useActiveOrderStore } from '../stores/active-order.store.js'
import { formatMoney } from '../lib/money.js'
import { NintaiGOHeader } from '../components/NintaiGOHeader.js'
import {
  DELIVER_TO_PREFIX,
  DEMO_PAYMENT_DELAY_MS,
  STADIUM_STEPS,
  formatSeat,
  stadiumEtaMinutes,
  stadiumPhase,
  stepIndex,
  useSeatStore,
  type StadiumStep,
} from '../lib/stadium.js'

/**
 * MODO ESTADIO — pedido en curso. Pasos Recibido → Preparando → Listo → En
 * camino → Entregado, derivados de los estados REALES (ver `stadiumPhase`):
 * no hay estados nuevos ni migraciones. El código de recolección es el código
 * de verificación de entrega: quien entrega lo teclea en el POS (Para llevar ›
 * Entregar pedido) y al validar, esta pantalla pasa a "¡Disfruta!".
 */
const POLL_MS = 4000

const STEP_COPY: Record<StadiumStep, { title: string; sub: string; icon: string }> = {
  received: { title: 'Recibimos tu pedido', sub: 'El local ya lo tiene. En un momento entra a cocina.', icon: '🧾' },
  preparing: { title: 'Tu pedido se está preparando', sub: 'En breve estará en camino a tu asiento.', icon: '👨‍🍳' },
  ready: { title: '¡Tu pedido está listo!', sub: 'Lo están empacando para llevártelo.', icon: '🛍️' },
  on_the_way: { title: 'Tu pedido va en camino', sub: 'El personal del local va hacia tu asiento.', icon: '🏃' },
  delivered: { title: '¡Disfruta!', sub: 'Tu pedido ha llegado a tu asiento.', icon: '✓' },
}

/** Ubicación tal como viajó en el pedido (nota del ítem); si no, la guardada. */
function seatFromOrder(order: OrderDto | null): string | null {
  for (const it of order?.items ?? []) {
    const i = it.notes?.indexOf(DELIVER_TO_PREFIX) ?? -1
    if (it.notes && i >= 0) return it.notes.slice(i + DELIVER_TO_PREFIX.length).split('|')[0]!.trim()
  }
  return null
}

export function StadiumTracker() {
  const { slug, orderId } = useParams<{ slug: string; orderId: string }>()
  const navigate = useNavigate()
  const session = useSessionStore((s) => s.session)
  const seat = useSeatStore((s) => (slug ? s.bySlug[slug] ?? null : null))
  const [order, setOrder] = useState<OrderDto | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [qr, setQr] = useState<string | null>(null)
  const [retrying, setRetrying] = useState(false)

  const token = session?.token
  const refetch = useCallback(async (): Promise<void> => {
    if (!token || !orderId) return
    const r = await getOrder(token, orderId)
    if (!r.ok) { setError(r.error.message); return }
    setOrder(r.data)
    setError(null)
    if (r.data.deliveredAt || r.data.status === 'CANCELLED') {
      useActiveOrderStore.getState().removeByOrderId(r.data.id)
    }
  }, [token, orderId])

  useEffect(() => {
    if (!session) {
      navigate(`/r/${slug}`, { replace: true })
      return
    }
    void refetch()
    const iv = setInterval(() => void refetch(), POLL_MS)
    return () => clearInterval(iv)
  }, [session, slug, navigate, refetch])

  // Mismo canal privado que el seguimiento normal: aceptar/cocina/entrega llegan al instante.
  useRealtimeSubscription(
    session?.tableSessionId ? `ts:${session.tableSessionId}:orders` : '',
    () => { void refetch() },
    [session?.tableSessionId, refetch],
  )

  const code = order?.pickupCode ?? null
  useEffect(() => {
    if (!code) { setQr(null); return }
    let alive = true
    QRCode.toDataURL(code, { margin: 1, width: 220, color: { dark: '#0B2230', light: '#FFFFFF' } })
      .then((u) => { if (alive) setQr(u) })
      .catch(() => { if (alive) setQr(null) })
    return () => { alive = false }
  }, [code])

  if (!session) return <></>

  async function retryPayment(): Promise<void> {
    if (!token || !orderId || retrying) return
    setRetrying(true)
    const [r] = await Promise.all([payOrderDemo(token, orderId), new Promise((res) => setTimeout(res, DEMO_PAYMENT_DELAY_MS))])
    setRetrying(false)
    if (!r.ok) setError(r.error.message)
    await refetch()
  }

  const phase = order ? stadiumPhase(order) : null
  const step: StadiumStep | null = phase?.kind === 'step' ? phase.step : null
  const current = step ? stepIndex(step) : -1
  const delivered = step === 'delivered'
  const paid = !!order && order.totalCents > 0 && (order.paidCents ?? 0) >= order.totalCents
  const where = seatFromOrder(order) ?? (seat ? formatSeat(seat) : null)
  const liveItems = (order?.items ?? []).filter((it) => it.status !== 'CANCELLED')
  const copy = step ? STEP_COPY[step] : null

  return (
    <div className="app st-app">
      <NintaiGOHeader slug={slug} tableCode={session.tableCode} branchId={session.branchId} />
      <div className="container st-pad-bottom">
        <section className={`st-track${delivered ? ' is-done' : ''}`}>
          <div className="st-track__head">
            <span className="st-track__eyebrow">Pedido en curso</span>
            <span className="st-track__folio">#{order?.folio ?? '—'}</span>
          </div>

          <ol className="st-steps" aria-label="Estado del pedido">
            {STADIUM_STEPS.map((s, i) => {
              const state = current < 0 ? 'todo' : i < current ? 'done' : i === current ? 'now' : 'todo'
              return (
                <li key={s.key} className={`st-step st-step--${state}`} aria-current={state === 'now' ? 'step' : undefined}>
                  <span className="st-step__dot" aria-hidden>{state === 'done' || (state === 'now' && delivered) ? '✓' : i + 1}</span>
                  <span className="st-step__label">{s.label}</span>
                </li>
              )
            })}
          </ol>

          {!order && !error && (
            <div className="st-track__status"><span className="spinner" /> Cargando tu pedido…</div>
          )}
          {phase?.kind === 'cancelled' && (
            <div className="st-track__status"><strong>Tu pedido fue cancelado.</strong> Acércate al local si tienes dudas.</div>
          )}
          {phase?.kind === 'rejected' && (
            <div className="st-track__status"><strong>El local no pudo aceptar tu pedido.</strong></div>
          )}
          {phase?.kind === 'awaiting_payment' && (
            <div className="st-track__status">
              <strong>Falta confirmar tu pago.</strong>
              <button type="button" className="st-btn st-btn--primary" disabled={retrying} onClick={() => void retryPayment()}>
                {retrying ? <><span className="spinner" /> Procesando pago…</> : 'Pagar ahora (demostración)'}
              </button>
            </div>
          )}
          {copy && (
            <div className="st-track__card">
              <div className={`st-track__icon${delivered ? ' is-done' : ''}`} aria-hidden>{copy.icon}</div>
              <div className="st-track__title">{copy.title}</div>
              <div className="st-track__sub">{copy.sub}</div>
              {!delivered && step && (
                <div className="st-eta">
                  <span>Tiempo estimado de entrega</span>
                  <strong>{stadiumEtaMinutes(step)} min</strong>
                </div>
              )}
            </div>
          )}
        </section>

        {code && !delivered && phase?.kind === 'step' && (
          <section className="st-card st-code">
            <div className="st-code__eyebrow">Código de verificación</div>
            <div className="st-code__value" aria-label="Código de verificación">{code}</div>
            {qr && <img className="st-code__qr" src={qr} alt={`Código ${code}`} width={132} height={132} />}
            <p className="st-code__hint"><strong>Muéstralo a quien te entregue.</strong> Con él confirma que el pedido es tuyo.</p>
          </section>
        )}

        {delivered && (
          <section className="st-card st-done">
            <div className="st-check" aria-hidden>✓</div>
            <div className="st-done__title">¡Entregado!</div>
            <p className="st-muted">Gracias por pedir desde tu asiento. ¡Disfruta el partido!</p>
            <Link to={`/r/${slug}/menu`} className="st-btn st-btn--primary">Pedir algo más</Link>
          </section>
        )}

        {where && (
          <section className="st-card st-seat-view">
            <span className="st-seat-view__pin" aria-hidden>📍</span>
            <div>
              <div className="st-muted">Entregar en</div>
              <div className="st-seat-view__main">{where}</div>
            </div>
          </section>
        )}

        {order && (
          <section className="st-card">
            <div className="st-card__head">
              <h2 className="st-card__title">Tu pedido</h2>
              {paid && <span className="st-paid">✓ Pagado · demostración</span>}
            </div>
            {liveItems.map((it) => (
              <div key={it.id} className="st-line st-line--compact">
                <div className="st-line__body">
                  <div className="st-line__name">{it.quantity}× {(it.productName ?? it.name) ?? 'Producto'}</div>
                  {it.modifiers && it.modifiers.length > 0 && (
                    <div className="st-line__meta">{it.modifiers.map((m) => m.name).join(' · ')}</div>
                  )}
                </div>
                <div className="st-line__price">
                  {formatMoney((it.unitPriceCents + (it.modifiers ?? []).reduce((s, m) => s + m.priceDeltaCents, 0)) * it.quantity)}
                </div>
              </div>
            ))}
            <div className="st-totals">
              <div className="st-totals__row st-totals__row--total"><span>Total</span><span>{formatMoney(order.totalCents)}</span></div>
            </div>
          </section>
        )}

        {error && <div className="st-alert" role="alert">No pudimos actualizar tu pedido: {error}</div>}

        {!delivered && (
          <Link to={`/r/${slug}/menu`} className="st-btn st-btn--ghost">Seguir viendo el menú</Link>
        )}
      </div>
      <div className="footer">Powered by Nintai<em>GO</em> · en vivo</div>
    </div>
  )
}
